import { Injectable } from '@angular/core';
import {
  MyHttpClientFactory,
  ResponseCasingEnum,
  environment,
  LocalStorageService,
} from '@ups/xplat/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import {
  DropdownEmployeeDetailDto,
  EmployeeDropdownDto,
  IODataResult,
  ProfileModel,
} from '@ups/xplat/api/dto';

const FAVORITE_EMPLOYEE = '/api/spartaemployee/employee/favorite';
const FAVORITES_EMPLOYEE = '/api/spartaemployee/employee/favorites';

@Injectable({
  providedIn: 'root',
})
export class SearchEmployeeService {
  private http: HttpClient;
  private profile: ProfileModel;

  constructor(
    private clientFactory: MyHttpClientFactory,
    private storage: LocalStorageService
  ) {
    this.http = this.clientFactory.createHttpClient(
      environment.urls.spartaAPI,
      true,
      ResponseCasingEnum.PascalCase
    );
    this.profile = this.storage.getItem('profile') as ProfileModel;
  }

  search(
    searchString: string,
    params: HttpParams = null,
    limit: number = 20,
    showInActive: boolean | null = null,
    isActiveToggle = null,
    favoriteType: string | null = null,
    favoriteVpCompanyID: number | null = null
  ) {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    params = params || new HttpParams();
    params = params.append('searchString', searchString);
    params = params.append('take', limit);
    if (favoriteType) {
      params = params.append('favoriteType', favoriteType);
    }
    if (favoriteType && favoriteVpCompanyID != null) {
      // US 36258: the API flags this company's safety contacts as recommended
      params = params.append('favoriteVpCompanyID', favoriteVpCompanyID);
    }
    if (showInActive && showInActive === true) {
      // isolated for serviceRequestForm
      if (isActiveToggle) {
        if (showInActive && showInActive === true) {
        } else {
          params = params.append('isActive', true);
        }
      }
    }

    return this.http
      .get(`/api/spartaemployee/employee/dropdown`, {
        params,
      })
      .pipe(
        map((data) => {
          if (data && Array.isArray(data)) {
            // only include valid data
            return data.filter((d) => !!d.FirstName || !!d.LastName);
          }
          return data;
        })
      );
  }

  getFavoriteEmployeeHRRefs(
    auth0UserId: string,
    type: string
  ): Promise<number[]> {
    const params = new HttpParams().append('type', type);
    return this.http
      .get<number[]>(`${FAVORITES_EMPLOYEE}/${auth0UserId}`, { params })
      .toPromise();
  }

  addOrRemoveFavoriteEmployee(
    hrRef: number,
    type: string,
    auth0UserId?: string
  ): Promise<unknown> {
    return this.http
      .post(FAVORITE_EMPLOYEE, {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        UserID: auth0UserId ?? this.profile?.auth0UserId,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        HRRef: hrRef,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Type: type,
      })
      .toPromise();
  }

  searchByFacility(
    facilityId: number
  ): Observable<IODataResult<DropdownEmployeeDetailDto>> {
    return this.http.get<IODataResult<DropdownEmployeeDetailDto>>(
      `/api/spartaemployee/employeesByFacility/${facilityId}`
    );
  }

  /**
   * WI #31500 - resolve a single employee by their Sparta employee GUID (the id
   * carried in a scanned training-record QR).
   *
   * Reuses the EXISTING, already-authenticated employee/dropdown endpoint, which
   * narrows to the single matching employee when its searchString is a GUID - so
   * there is no new by-GUID access path. Resolves null when the GUID matches no
   * employee, so an unknown QR is reported as "not found" rather than adding a
   * blank row (AC4).
   */
  getById(employeeId: string): Observable<EmployeeDropdownDto | null> {
    const params = new HttpParams()
      .append('searchString', employeeId)
      .append('take', '1');

    return this.http
      .get<EmployeeDropdownDto[]>(`/api/spartaemployee/employee/dropdown`, {
        params,
      })
      .pipe(
        map((data) => (Array.isArray(data) && data.length > 0 ? data[0] : null))
      );
  }
}
